import {
  accountActivation,
  resendActivation,
  signout,
  validateActivationCode,
  ActivationCodeExpirationResponse,
} from '../services/auth.service';
import { AccountActivationPayload } from '../types/auth.type';
import { useGlobalMutation } from '../../../state/query-client/hooks';
import { ErrorResponse } from '../../../hooks/use-error-handler';

/**
 * Authentication Mutations
 *
 * A collection of reusable hooks for handling authentication-related operations using `useGlobalMutation`.
 * These hooks manage server communication, error handling, and success feedback for various auth flows,
 * including sign-out and account activation. Sign-in itself is IAM's hosted (OIDC) login.
 *
 * Hooks:
 * - `useSignoutMutation`: Handles user sign-out
 * - `useAccountActivation`: Activates an invited account
 * - `useResendActivation`: Resends account activation link to user's email
 *
 * @module authMutations
 */

export const useSignoutMutation = () => {
  return useGlobalMutation({
    mutationKey: ['signout'],
    mutationFn: signout,
    onError: (error) => {
      throw error;
    },
  });
};

export const useAccountActivation = () => {
  return useGlobalMutation<unknown, ErrorResponse, AccountActivationPayload>({
    mutationKey: ['accountActivation'],
    mutationFn: accountActivation,
    onError: (error) => {
      throw error;
    },
  });
};

export const useResendActivation = () => {
  return useGlobalMutation<unknown, ErrorResponse, { userId: string; projectKey?: string }>({
    mutationKey: ['resendActivation'],
    mutationFn: resendActivation,
    onError: (error) => {
      throw error;
    },
  });
};

export const useValidateActivationCodeMutation = () => {
  return useGlobalMutation<
    ActivationCodeExpirationResponse,
    ErrorResponse,
    { activationCode: string; projectKey: string }
  >({
    mutationKey: ['validateActivationCode'],
    mutationFn: validateActivationCode,
    onError: (error) => {
      throw error;
    },
  });
};
